import {
  ApiMessageStack,
  DateRangePickerInput,
  FormControlError,
  FormControlMeta,
  FormControlV2,
  Panel,
  PanelBody,
  PanelHeader,
  PanelTitle,
} from "components";
import React, {
  ChangeEvent,
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Box,
  Button,
  Flex,
  HStack,
  SimpleGrid,
  Switch,
  Text,
  useToast,
  VStack,
} from "@chakra-ui/react";
import { FormControlsTypeEnum } from "enums/form-controls-type.enum";
import * as Yup from "yup";
import { REQUIRED_FIELD } from "constants/validator-messages";
import {
  DEFAULT_ERROR_TOAST_OPTIONS,
  DEFAULT_SUCCESS_TOAST_OPTIONS,
} from "constants/default-toast-options";
import { observer } from "mobx-react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup/dist/yup";
import { ExportRequestList } from "./ExportRequestList";
import { AccountExportRequestsStore } from "store/UserAccounts/UserAccount/AccountExportStore/AccountExportRequestsStore.store";
import { ApiRequestStatusEnum } from "enums/api-request-status.enum";
import { IGetOptionPropsData } from "components/select-input/SelectInputOptionsController";
import { ExportSelectPeriodOption } from "components/select-input/custom-options/ExportSelectPeriodOption";
import {
  getExportDateRangeDays,
  getExportDateRangePeriod,
  isDateCorrectFormat,
} from "utils/date.utils";
import {
  DATE_YEAR,
  DATE_YEAR_MONTH,
  EXPORT_DATE_RANGE_MAX_DAYS,
} from "constants/date";
import { faInfoCircle } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { TSerializedDateRange } from "types/date.type";
import moment from "moment";

const REFRESH_INTERVAL = 20000;
const DEFAULT_TITLE = "Export";
const CUSTOM_DATE_RANGE_LABEL = "Custom date range";
const DATE_RANGE_TOO_LONG_ERROR = {
  type: "validate",
  message: "Date range cannot exceed a year",
};
const EMPTY_DATE_RANGE: TSerializedDateRange = {
  startDate: null,
  endDate: null,
};

type TFieldValues = {
  period: string;
  isCustomDateRange: boolean;
};

const initialValues: TFieldValues = {
  period: "",
  isCustomDateRange: false,
};

const validationSchema = Yup.object().shape({
  isCustomDateRange: Yup.boolean(),
  period: Yup.string().when("isCustomDateRange", {
    is: false,
    then: Yup.string().required(REQUIRED_FIELD),
  }),
});

interface IProps {
  exportRequestsStore: AccountExportRequestsStore;
  title?: string;
  allowCustomDateRange?: boolean;
  minDate?: string;
}

export const Export: FC<IProps> = observer(
  ({
    exportRequestsStore,
    title = DEFAULT_TITLE,
    allowCustomDateRange = false,
    minDate,
  }) => {
    const [loadingStatus, setLoadingStatus] = useState(
      ApiRequestStatusEnum.NONE
    );
    const [dateRange, setDateRange] = useState(EMPTY_DATE_RANGE);
    const refreshInterval = useRef<
      ReturnType<typeof setInterval> | undefined
    >();
    const toast = useToast();

    const exportRequests = exportRequestsStore.exportRequestsArray || [];
    const areTherePendingExportRequests =
      exportRequestsStore.areTherePendingExportRequests;
    const exportPeriodOptions = exportRequestsStore.exportPeriodOptions;

    const {
      handleSubmit,
      control,
      watch,
      reset,
      setValue,
      formState: { isSubmitting },
    } = useForm<TFieldValues>({
      defaultValues: initialValues,
      mode: "onSubmit",
      resolver: yupResolver(validationSchema),
    });

    const period = watch("period");
    const isCustomDateRange = watch("isCustomDateRange");

    const { startDate, endDate } = dateRange;
    const hasDateRange = !!startDate && !!endDate;

    const isDateRangeTooLong =
      hasDateRange &&
      getExportDateRangeDays(startDate, endDate) > EXPORT_DATE_RANGE_MAX_DAYS;

    const dateRangePeriod =
      hasDateRange && !isDateRangeTooLong
        ? getExportDateRangePeriod(startDate, endDate)
        : null;

    const exportPeriod = isCustomDateRange ? dateRangePeriod : period;

    const handleCustomDateRangeToggle = useCallback(
      (event: ChangeEvent<HTMLInputElement>) => {
        setValue("isCustomDateRange", event.target.checked);
        setDateRange(EMPTY_DATE_RANGE);
        setValue("period", initialValues.period);
      },
      [setValue]
    );

    const submitButtonLabel = useMemo(() => {
      if (isCustomDateRange)
        return !!dateRangePeriod ? "Export the selected dates" : "Export";
      if (isDateCorrectFormat(period, DATE_YEAR))
        return "Export the selected year";
      if (isDateCorrectFormat(period, DATE_YEAR_MONTH))
        return "Export the selected month";
      return "Export";
    }, [period, isCustomDateRange, dateRangePeriod]);

    const onSubmit = useCallback(async () => {
      try {
        if (!!exportPeriod) {
          await exportRequestsStore.createExportRequest(exportPeriod);
          reset();
          setDateRange(EMPTY_DATE_RANGE);
          toast({
            ...DEFAULT_SUCCESS_TOAST_OPTIONS,
            description:
              "We are preparing your export. Download will become automatically available once it is ready.",
          });
        }
      } catch (e) {
        toast({
          ...DEFAULT_ERROR_TOAST_OPTIONS,
          description: <ApiMessageStack messageStack={e.message} />,
        });
      }
    }, [exportRequestsStore, toast, reset, exportPeriod]);

    const fetchExportRequests = useCallback(async () => {
      try {
        setLoadingStatus(ApiRequestStatusEnum.PENDING);
        await exportRequestsStore.fetchExportRequests();
        setLoadingStatus(ApiRequestStatusEnum.SUCCESS);
      } catch (error) {
        setLoadingStatus(ApiRequestStatusEnum.ERROR);
      }
    }, [exportRequestsStore]);

    useEffect(() => {
      fetchExportRequests();
    }, [fetchExportRequests]);

    useEffect(() => {
      if (refreshInterval.current) clearInterval(refreshInterval.current);

      if (areTherePendingExportRequests) {
        refreshInterval.current = setInterval(
          fetchExportRequests,
          REFRESH_INTERVAL
        );
      }
    }, [areTherePendingExportRequests, fetchExportRequests]);

    return (
      <Panel overflow={"visible"}>
        <PanelHeader>
          <PanelTitle>{title}</PanelTitle>
        </PanelHeader>
        <form onSubmit={handleSubmit(onSubmit)}>
          <PanelBody>
            <VStack spacing={2} align={"flex-end"} mb={10}>
              <SimpleGrid columns={2} spacing={4}>
                <FormControlMeta
                  label={"Export to a CSV file"}
                  description={
                    "Select a period to export it’s data. When an export is requested it can take a few minutes to prepare. Once the export is ready to be downloaded it will appear here."
                  }
                />
                {isCustomDateRange ? (
                  <VStack spacing={2} align={"flex-start"}>
                    <DateRangePickerInput
                      startDate={dateRange.startDate}
                      endDate={dateRange.endDate}
                      onDatesChange={setDateRange}
                      minDate={minDate}
                      maxDate={moment()
                        .endOf("day")
                        .toISOString()}
                      clearable={true}
                      disabled={isSubmitting}
                      width={"100%"}
                    />
                    {isDateRangeTooLong && (
                      <FormControlError error={DATE_RANGE_TOO_LONG_ERROR} />
                    )}
                  </VStack>
                ) : (
                  <FormControlV2<TFieldValues>
                    name={"period"}
                    control={control}
                    type={FormControlsTypeEnum.SINGLE_SELECT}
                    additionalProps={{
                      options: exportPeriodOptions,
                      placeholder: "Select date",
                      optionComponent: (
                        optionProps: IGetOptionPropsData<string>
                      ) => (
                        <ExportSelectPeriodOption optionProps={optionProps} />
                      ),
                    }}
                  />
                )}
                {allowCustomDateRange && (
                  <>
                    <FormControlMeta label={CUSTOM_DATE_RANGE_LABEL} />
                    <Flex alignItems={"center"} justifyContent={"flex-end"}>
                      <Switch
                        aria-label={CUSTOM_DATE_RANGE_LABEL}
                        isChecked={isCustomDateRange}
                        isDisabled={isSubmitting}
                        onChange={handleCustomDateRangeToggle}
                      />
                    </Flex>
                  </>
                )}
              </SimpleGrid>
              <Button
                type={"submit"}
                colorScheme={"blue"}
                isDisabled={isSubmitting || !exportPeriod}
                isLoading={isSubmitting}
              >
                {submitButtonLabel}
              </Button>
              {!exportPeriod && (
                <HStack>
                  <Box opacity={0.6}>
                    <FontAwesomeIcon icon={faInfoCircle} fontSize={16} />
                  </Box>
                  <Text fontSize={"xs"}>Select an export option</Text>
                </HStack>
              )}
            </VStack>
            <ExportRequestList
              exportRequests={exportRequests}
              loadingStatus={loadingStatus}
            />
          </PanelBody>
        </form>
      </Panel>
    );
  }
);
